import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSpc } from '../../context/SpcContext';
import { Reveal } from '../ui';
import { ArrowLeft, Gamepad2, Swords, Users, Check, X as XIcon, Trophy, Loader2, Bot as BotIcon, Zap, Brain, Footprints, Music4, Flag } from 'lucide-react';
import TicTacToeBot from '../solo/TicTacToeBot';
import MemoryMatch from '../solo/MemoryMatch';
import ReactionTime from '../solo/ReactionTime';
import Trivia from '../solo/Trivia';
import StepRace from '../solo/StepRace';
import Sequence from '../solo/Sequence';
import TugOfWar from '../solo/TugOfWar';
import RaceDay from '../solo/RaceDay';

function Board({ board, winningLine, myTurn, onPlay, disabled }) {
  return (
    <div className="oxo-board" data-testid="oxo-board">
      {board.map((c, i) => (
        <button key={i} data-testid={`oxo-cell-${i}`}
          className={`oxo-cell ${c ? 'filled ' + c.toLowerCase() : ''} ${winningLine && winningLine.includes(i) ? 'win' : ''}`}
          disabled={disabled || !!c || !myTurn}
          onClick={() => onPlay(i)}>
          {c}
        </button>
      ))}
    </div>
  );
}

function GameCard({ g, ctx, refresh, backToGames }) {
  const act = async (fn) => { const r = await fn(); return r; };
  const play = async (i) => { try { await ctx.makeMove(g.id, i); refresh(); } catch (e) { ctx.toast(e?.response?.data?.detail || 'Move failed'); refresh(); } };
  const join = async () => { try { await ctx.joinGame(g.id); ctx.toast('Game on!'); refresh(); } catch (e) { ctx.toast(e?.response?.data?.detail || 'Could not join'); refresh(); } };
  const leave = async (msg) => { try { await ctx.leaveGame(g.id); if (msg) ctx.toast(msg); refresh(); } catch (e) { refresh(); } };

  let statusLine, statusClass = '';
  if (g.status === 'finished') {
    statusClass = g.winner === 'me' ? 'win' : g.winner === 'draw' ? 'draw' : 'lose';
    statusLine = g.winner === 'me' ? 'You won! 🏆' : g.winner === 'draw' ? "It's a draw" : `${g.opponentName} won`;
  } else if (g.isChallengee) {
    statusLine = `${g.opponentName} challenged you`;
  } else if (g.sentByMe) {
    statusLine = `Waiting for ${g.opponentName} to accept…`;
  } else if (g.status === 'open') {
    statusLine = 'Open game — waiting for a player…';
  } else {
    statusLine = g.myTurn ? 'Your move' : `${g.turnName || g.opponentName}'s move`;
  }

  const showBoard = g.status === 'active' || g.status === 'finished';
  return (
    <div className="card game-card" data-testid={`game-${g.id}`}>
      <button className="back-to-games" data-testid={`back-to-games-${g.id}`} onClick={backToGames}><ArrowLeft size={13} /> Back to Games</button>
      <div className="game-top">
        <div className="game-vs">
          <span className="game-badge">O&amp;X</span>
          <div>
            <strong>vs {g.opponentName}</strong>
            <span className={`game-status ${statusClass}`} data-testid={`game-status-${g.id}`}>{statusLine}</span>
          </div>
        </div>
        {g.mySymbol && <span className="game-symbol">You&nbsp;are&nbsp;<b>{g.mySymbol}</b></span>}
      </div>

      {showBoard && <Board board={g.board} winningLine={g.winningLine} myTurn={g.myTurn && g.status === 'active'} onPlay={play} disabled={g.status !== 'active'} />}

      <div className="game-actions">
        {g.isChallengee && <>
          <button className="btn" data-testid={`accept-${g.id}`} onClick={join}><Check size={15} /> Accept</button>
          <button className="btn ghost" data-testid={`decline-${g.id}`} onClick={() => leave('Challenge declined')}><XIcon size={15} /> Decline</button>
        </>}
        {(g.sentByMe || (g.status === 'open' && g.createdByMe)) && <button className="btn secondary" data-testid={`cancel-game-${g.id}`} onClick={() => leave('Game cancelled')}>Cancel</button>}
        {g.status === 'active' && <button className="btn ghost" data-testid={`resign-${g.id}`} onClick={() => { if (window.confirm('Resign this game?')) leave('You resigned'); }}>Resign</button>}
        {g.status === 'finished' && <button className="btn secondary" data-testid={`remove-game-${g.id}`} onClick={() => leave()}>Remove</button>}
      </div>
    </div>
  );
}

export default function Games() {
  const ctx = useSpc();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opp, setOpp] = useState('');
  const [tab, setTab] = useState('multiplayer'); // solo | multiplayer
  const [soloGame, setSoloGame] = useState('raceday'); // raceday | oxo | trivia | steprace | tugofwar | sequence | memory | reaction
  const topRef = useRef(null);
  const backToGames = useCallback(() => { topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, []);

  const refresh = useCallback(async (poll) => {
    try {
      const res = await ctx.getGames(poll);
      // Polled refreshes omit the (heavier) opponents roster — keep the one we already have.
      setData(prev => (poll && prev) ? { ...res.data, opponents: prev.opponents } : res.data);
    } catch (e) {}
  }, [ctx]);

  const refreshPresence = useCallback(async () => {
    try {
      const res = await ctx.getGamesPresence();
      const onlineIds = new Set(res.data.opponents.filter(o => o.online).map(o => o.id));
      setData(prev => {
        if (!prev?.opponents?.length) return prev;
        const next = prev.opponents.map(o => ({ ...o, online: onlineIds.has(o.id) }));
        next.sort((a, b) => (a.online === b.online ? a.name.localeCompare(b.name) : a.online ? -1 : 1));
        return { ...prev, opponents: next };
      });
    } catch (e) {}
  }, [ctx]);

  useEffect(() => {
    let alive = true;
    (async () => { await refresh(false); if (alive) setLoading(false); })();
    const id = setInterval(() => refresh(true), 2500);
    const presenceId = setInterval(refreshPresence, 15000);
    return () => { alive = false; clearInterval(id); clearInterval(presenceId); };
  }, [refresh, refreshPresence]);

  const challenge = async (idOverride) => {
    const targetId = idOverride || opp;
    if (!targetId) return;
    try { await ctx.createGame(targetId); ctx.toast('Challenge sent!'); setOpp(''); refresh(false); }
    catch (e) { ctx.toast(e?.response?.data?.detail || 'Could not send challenge'); }
  };
  const openGame = async () => {
    try { await ctx.createGame(null); ctx.toast('Open game created — waiting for a player'); refresh(false); }
    catch (e) { ctx.toast('Could not create game'); }
  };

  const yours = data?.yourGames || [];
  const opens = data?.openGames || [];
  const opponents = data?.opponents || [];
  const active = yours.filter(g => g.status === 'active' || g.status === 'open');
  const finished = yours.filter(g => g.status === 'finished');

  return (
    <div data-testid="games-view">
      <Reveal className="section-head">
        <div><div className="eyebrow">Arcade</div><h2>SPC Arcade</h2><p>Play against the Coach Bot on your own, or challenge a fellow SPC member to a live game.</p></div>
      </Reveal>

      <div className="arcade-tabs" role="tablist" data-testid="arcade-tabs" ref={topRef}>
        <button role="tab" className={`arcade-tab ${tab === 'solo' ? 'active' : ''}`} onClick={() => setTab('solo')} data-testid="arcade-tab-solo"><BotIcon size={15} /> Play solo</button>
        <button role="tab" className={`arcade-tab ${tab === 'multiplayer' ? 'active' : ''}`} onClick={() => setTab('multiplayer')} data-testid="arcade-tab-multiplayer"><Swords size={15} /> Multiplayer</button>
      </div>

      {tab === 'solo' && (
        <>
          <div className="arcade-picker" role="tablist" data-testid="solo-picker">
            <button className={`arcade-pick ${soloGame === 'raceday' ? 'active' : ''}`} onClick={() => setSoloGame('raceday')} data-testid="pick-raceday"><Flag size={14} /> SPC Race Day</button>
            <button className={`arcade-pick ${soloGame === 'oxo' ? 'active' : ''}`} onClick={() => setSoloGame('oxo')} data-testid="pick-oxo"><BotIcon size={14} /> Noughts &amp; Crosses</button>
            <button className={`arcade-pick ${soloGame === 'trivia' ? 'active' : ''}`} onClick={() => setSoloGame('trivia')} data-testid="pick-trivia"><Brain size={14} /> SPC Trivia</button>
            <button className={`arcade-pick ${soloGame === 'steprace' ? 'active' : ''}`} onClick={() => setSoloGame('steprace')} data-testid="pick-steprace"><Footprints size={14} /> Step Race</button>
            <button className={`arcade-pick ${soloGame === 'tugofwar' ? 'active' : ''}`} onClick={() => setSoloGame('tugofwar')} data-testid="pick-tugofwar"><span style={{ fontSize: 14 }}>🪢</span> Tug of War</button>
            <button className={`arcade-pick ${soloGame === 'sequence' ? 'active' : ''}`} onClick={() => setSoloGame('sequence')} data-testid="pick-sequence"><Music4 size={14} /> Cadence</button>
            <button className={`arcade-pick ${soloGame === 'memory' ? 'active' : ''}`} onClick={() => setSoloGame('memory')} data-testid="pick-memory"><span style={{ fontSize: 15 }}>🧠</span> Memory Match</button>
            <button className={`arcade-pick ${soloGame === 'reaction' ? 'active' : ''}`} onClick={() => setSoloGame('reaction')} data-testid="pick-reaction"><Zap size={14} /> Reaction Time</button>
          </div>
          <Reveal delay={0.03} className="card solo-card">
            <button className="back-to-games" data-testid="back-to-games-solo" onClick={backToGames}><ArrowLeft size={13} /> Back to Games</button>
            {soloGame === 'raceday' && <RaceDay />}
            {soloGame === 'oxo' && <TicTacToeBot />}
            {soloGame === 'trivia' && <Trivia />}
            {soloGame === 'steprace' && <StepRace />}
            {soloGame === 'tugofwar' && <TugOfWar />}
            {soloGame === 'sequence' && <Sequence />}
            {soloGame === 'memory' && <MemoryMatch />}
            {soloGame === 'reaction' && <ReactionTime />}
          </Reveal>
        </>
      )}

      {tab === 'multiplayer' && (
        <>
          {opponents.some(o => o.online) && (
            <Reveal delay={0.02} className="card">
              <h4 style={{ margin: '0 0 4px' }}>🟢 Online now</h4>
              <p style={{ margin: '0 0 4px' }}>Tap a member to challenge them straight away.</p>
              <div className="online-members" data-testid="online-members-strip">
                {opponents.filter(o => o.online).map(o => (
                  <button key={o.id} type="button" className="online-member-chip" data-testid={`online-member-${o.id}`} onClick={() => challenge(o.id)}>
                    <span className="avatar-wrap">
                      {o.avatar ? <img src={o.avatar} alt={o.name} /> : <span className="avatar-fallback">{o.name?.[0]}</span>}
                      <span className="online-dot" />
                    </span>
                    <span>{o.name}</span>
                  </button>
                ))}
              </div>
            </Reveal>
          )}
          <Reveal delay={0.03} className="card game-new">
            <div className="game-new-row">
              <span className="game-new-ic"><Swords size={18} /></span>
              <select value={opp} onChange={(e) => setOpp(e.target.value)} data-testid="challenge-select">
                <option value="">Challenge a member…</option>
                {opponents.map(o => <option key={o.id} value={o.id}>{o.online ? '🟢 ' : ''}{o.name}</option>)}
              </select>
              <button className="btn" data-testid="challenge-btn" disabled={!opp} onClick={() => challenge()}>Challenge</button>
            </div>
            <div className="game-new-or"><span>or</span></div>
            <button className="btn secondary" data-testid="open-game-btn" onClick={openGame}><Users size={15} /> Create open game (anyone can join)</button>
          </Reveal>

          {loading ? (
            <div className="empty"><Loader2 size={20} className="spin" /> Loading your games…</div>
          ) : (
            <>
              {active.length > 0 && <div className="games-grid">{active.map(g => <GameCard key={g.id} g={g} ctx={ctx} refresh={refresh} backToGames={backToGames} />)}</div>}

              {opens.length > 0 && <>
                <Reveal className="section-head" style={{ marginTop: 28 }}><div><h3 style={{ margin: 0 }}>Open challenges</h3><p>Jump into a game someone's looking to play.</p></div></Reveal>
                <div className="games-grid">{opens.map(g => (
                  <div className="card game-card" key={g.id} data-testid={`open-game-${g.id}`}>
                    <div className="game-top"><div className="game-vs"><span className="game-badge">O&amp;X</span><div><strong>{g.opponentName}</strong><span className="game-status">Looking for a game</span></div></div></div>
                    <button className="btn" data-testid={`join-${g.id}`} onClick={async () => { try { await ctx.joinGame(g.id); ctx.toast('Game on!'); refresh(); } catch (e) { ctx.toast(e?.response?.data?.detail || 'Could not join'); refresh(); } }}>Join game</button>
                  </div>
                ))}</div>
              </>}

              {finished.length > 0 && <>
                <Reveal className="section-head" style={{ marginTop: 28 }}><div><h3 style={{ margin: 0 }}>Recent results</h3></div></Reveal>
                <div className="games-grid">{finished.map(g => <GameCard key={g.id} g={g} ctx={ctx} refresh={refresh} backToGames={backToGames} />)}</div>
              </>}

              {active.length === 0 && opens.length === 0 && finished.length === 0 && (
                <div className="empty" data-testid="games-empty"><Gamepad2 size={26} /><p>No games yet — challenge a member above, or switch to Play solo to face the Coach Bot.</p></div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
